import { useState } from 'react';
import { Button, Flex, Text } from '@mantine/core';
import {
	CustomDetailEnum,
	OrganizationCustomFieldResponseDto,
} from '@dealstack/shared/lib/generated';
import { useTranslationMipManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { MantineTabs } from '@dealstack/frontend/shared/components/MantineTabs/MantineTabs';
import { MantineTabProps } from '@dealstack/frontend/shared/components/MantineTabs/MantineTab.types';
import { CustomFieldsTable } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ProgrammeSettings/InvestorData/components/CustomFieldsTable/CustomFieldsTable';
import { OrganizationCustomFieldDrawer } from '@dealstack/frontend/app/pages/Organization/OrganizationProgrammeDefaults/components/OrganizationCustomFieldDrawer';
import { useOrganizationCustomFields } from '@dealstack/frontend/app/pages/Organization/OrganizationProgrammeDefaults/hooks/useOrganizationCustomFields';
import { InvestorDataRequirementsList } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ProgrammeSettings/InvestorData/components/InvestorDataRequirementsList';
import {
	KycSection,
	KycSettingsContainer,
} from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ProgrammeSettings/KycSettings/styles';
import { ENTITY_TABS } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ProgrammeSettings/KycSettings/types';
import { ProgrammeDefaultsEmptyState } from '@dealstack/frontend/app/pages/Organization/OrganizationProgrammeDefaults/components/ProgrammeDefaultsEmptyState';
import { useOrganizationRequirements } from '@dealstack/frontend/app/pages/Organization/OrganizationProgrammeDefaults/hooks/useOrganizationRequirements';

interface Props {
	organizationId: string;
	canEdit: boolean;
}

export const ProgrammeDefaultsInvestorData = ({ organizationId, canEdit }: Props) => {
	const tMipManage = useTranslationMipManage();
	const [activeTab, setActiveTab] = useState<ENTITY_TABS>(ENTITY_TABS.INDIVIDUAL);
	const [drawerOpened, setDrawerOpened] = useState(false);
	const [editingField, setEditingField] = useState<OrganizationCustomFieldResponseDto | null>(
		null
	);
	const requirements = useOrganizationRequirements({ organizationId, canEdit });
	const customFields = useOrganizationCustomFields(organizationId);

	const investorTypeForTab =
		activeTab === ENTITY_TABS.INDIVIDUAL
			? CustomDetailEnum.INDIVIDUAL
			: CustomDetailEnum.VEHICLE;
	const fieldsForTab = customFields.customFields.filter(
		(field) => field.type === investorTypeForTab
	);

	const handleAddField = () => {
		setEditingField(null);
		setDrawerOpened(true);
	};

	const handleEditField = (field: OrganizationCustomFieldResponseDto) => {
		setEditingField(field);
		setDrawerOpened(true);
	};

	const tabsConfig: MantineTabProps<ENTITY_TABS>[] = [
		{
			label: tMipManage('kyc_settings.label.individual'),
			value: ENTITY_TABS.INDIVIDUAL,
			icon: 'information',
		},
		{
			label: tMipManage('kyc_settings.label.vehicle'),
			value: ENTITY_TABS.INVESTMENT_VEHICLE,
			icon: 'derived_commuted_structure',
		},
	];

	if (requirements.isLoading) {
		return null;
	}

	if (!requirements.isConfigured) {
		return (
			<ProgrammeDefaultsEmptyState
				canEdit={canEdit}
				onStart={requirements.startFromDefaults}
			/>
		);
	}

	return (
		<KycSettingsContainer>
			<MantineTabs
				tabs={tabsConfig}
				panels={[]}
				onlyDividerBorder={true}
				value={activeTab}
				onTabChange={(value) => setActiveTab(value as ENTITY_TABS)}
			>
				<KycSection>
					<InvestorDataRequirementsList
						type={activeTab}
						individualSettings={requirements.individualSettings}
						vehicleSettings={requirements.vehicleSettings}
						toggleIndividualSetting={requirements.toggleIndividualSetting}
						toggleVehicleSetting={requirements.toggleVehicleSetting}
						hasPermission={requirements.hasPermission}
					/>

					<Flex justify="space-between" gap="1rem" mt="sm">
						<Text fw={600}>
							{tMipManage('investor_data.custom_fields.section_title')}
						</Text>

						{canEdit && (
							<Button onClick={handleAddField} variant="default">
								{tMipManage('investor_data.custom_fields.add_button')}
							</Button>
						)}
					</Flex>

					<CustomFieldsTable
						fields={fieldsForTab}
						isLoading={customFields.isLoading}
						hasPermission={canEdit}
						onEdit={handleEditField}
						onToggleChecked={({ customDetailId, isChecked }) =>
							customFields.update({
								customFieldId: customDetailId,
								body: { isChecked },
							})
						}
					/>
				</KycSection>
			</MantineTabs>

			<OrganizationCustomFieldDrawer
				opened={drawerOpened}
				onClose={() => setDrawerOpened(false)}
				activeTab={activeTab}
				editField={editingField}
				isSaving={customFields.isSaving}
				onCreate={(data) => customFields.create(data)}
				onUpdate={(customFieldId, body) => customFields.update({ customFieldId, body })}
				onDelete={(customFieldId) => customFields.remove(customFieldId)}
			/>
		</KycSettingsContainer>
	);
};
